import { useEffect, useRef, useState } from "react";

import {
  SHOWCASE_DESIGN_CONTRACT_VERSION,
  SHOWCASE_DESIGN_PARTS,
  showcaseDesignResourceOwner,
  toShowcaseDesignBooleanState,
} from "../../../lib/showcaseDesignContract";
import {
  normalizeReturnLink,
  openReturnLink,
  openTelegramLink,
  tgWebApp,
} from "../../../lib/tg";
import AutoOpenOverlay from "../../../ui/AutoOpenOverlay";
import { ProgressBar } from "../components/ProgressBar";
import { randomShowcaseIcon, ResourceCard } from "../components/ResourceCard";
import {
  setLocalStatus,
  usePreCheckPolling,
} from "../hooks/usePreCheckPolling";
import { userApi } from "../lib/api";
import type {
  PreCheckStatus,
  ShowcaseFlow,
  ShowcaseResource,
  SystemShowcaseResource,
  VerifyNextAction,
  VerifyOut,
  VerifyReason,
} from "../lib/types";

type Props = {
  flowToken: string;
  autoOpenResourceId?: number;
};

type LoadedShowcase = {
  flowToken: string;
  autoOpenResourceId: number | undefined;
  flow: ShowcaseFlow;
  resources: ShowcaseResource[];
  systemResource: SystemShowcaseResource | null;
  systemResourceCompleted: boolean;
  systemResourceOpenFailed: boolean;
  completedDeliveryResourceIds: number[];
  explicitVerificationRequired: boolean;
};

const SHOWCASE_RECONCILE_DEBOUNCE_MS = 80;
const SHOWCASE_RECONCILE_MIN_INTERVAL_MS = 2_000;
const SHOWCASE_RECONCILE_MAX_BACKOFF_MS = 30_000;
const EXPLICIT_VERIFY_MAX_ATTEMPTS = 3;
const EXPLICIT_VERIFY_MAX_AUTO_RETRY_SECONDS = 3;

type ShowcaseLoadError = {
  flowToken: string;
  autoOpenResourceId: number | undefined;
  message: string;
};

const AUTO_OPEN_LOADING_TEXT = "загрузка...";
const AUTO_OPEN_DONE_TEXT = "вы были переведены на ресурс";
const AUTO_OPEN_COMPLETED_TEXT = "ресурс уже выполнен";
const AUTO_OPEN_UNAVAILABLE_TEXT = "ресурс временно недоступен";
const AUTO_OPEN_VERIFY_REQUIRED_TEXT = "вернитесь в витрину и нажмите галочку";
const SHOWCASE_LOAD_ERROR_TEXT =
  "Не удалось загрузить витрину. Проверьте интернет и попробуйте ещё раз.";
type ClickOutcome =
  | "opened"
  | "completed"
  | "retryable"
  | "unavailable"
  | "verification_required"
  | "verification_temporarily_unavailable"
  | "stale";

type ResourceActionMessage = {
  epoch: number;
  tone: "error" | "hint";
  text: string;
};

const ALREADY_OPENED_HINT =
  "Ресурс уже открыт. Выполните задание и нажмите галочку";

// Финальная страница user-mode. Legacy поллит membership pre-check; explicit
// поллит только durable DB-сигналы, чтобы поздний UNSUBSCRIBE вернул карточку.
export function Showcase({ flowToken, autoOpenResourceId }: Props) {
  const isAutoOpenMode = Boolean(autoOpenResourceId);
  const [loadedShowcase, setLoadedShowcase] = useState<LoadedShowcase | null>(
    null,
  );
  const systemResourceIcon = useRef<string | undefined>(undefined);
  const completedSystemResourceKey = useRef<string | null>(null);
  const systemResourceAnchor = useRef<{
    flowToken: string;
    revision: string;
    precedingResourceIds: Set<number>;
  } | null>(null);
  const [loadError, setLoadError] = useState<ShowcaseLoadError | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  // Локальный «оптимистичный» снапшот: перекрывает backend state после action.
  const [local, setLocal] = useState<Record<number, PreCheckStatus>>({});
  const [storedResourceActionMessages, setResourceActionMessages] = useState<
    Record<number, ResourceActionMessage>
  >({});
  const resourceActionMessagesContext = useRef({
    flowToken,
    autoOpenResourceId,
  });
  const [busyResourceIds, setBusyResourceIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [pendingDeliveryState, setPendingDeliveryState] = useState(() => ({
    flowToken,
    deliveries: readPendingDeliveries(flowToken),
  }));
  const [preCheckPollNowKey, setPreCheckPollNowKey] = useState(0);
  const [preCheckReset, setPreCheckReset] = useState<{
    resourceId?: number;
    key: number;
  }>({ key: 0 });
  const [autoOpenText, setAutoOpenText] = useState<string | null>(
    autoOpenResourceId ? AUTO_OPEN_LOADING_TEXT : null,
  );
  const [autoOpenRetryable, setAutoOpenRetryable] = useState(false);
  const autoOpenAttempted = useRef(false);
  const explicitAutoOpenClickRetryResourceId = useRef<number | null>(null);
  const autoVerifyAttempted = useRef(false);
  const autoVerifyArmed = useRef(false);
  const autoVerifyCleanup = useRef<(() => void) | null>(null);
  const assignedTasksObserved = useRef({ flowToken, observed: false });
  const pendingDeliveryRef = useRef(pendingDeliveryState);
  const busyResourceActions = useRef(new Map<number, number>());
  const nextResourceActionToken = useRef(0);
  const reconciliationContext = useRef({ flowToken, autoOpenResourceId });
  const resourceActionGeneration = useRef(0);
  const latestResourceActionEpochs = useRef(new Map<number, number>());
  const alreadyOpenedHintRequests = useRef(new Map<number, number>());
  const reconciliationRequestedAfterAction = useRef(false);
  const scheduleShowcaseReconciliation = useRef<(() => void) | null>(null);
  const resourceActionsMounted = useRef(true);
  const explicitVerifyRetryWaiters = useRef(new Set<() => void>());
  reconciliationContext.current = { flowToken, autoOpenResourceId };
  const resourceActionMessages =
    resourceActionMessagesContext.current.flowToken === flowToken &&
    resourceActionMessagesContext.current.autoOpenResourceId ===
      autoOpenResourceId
      ? storedResourceActionMessages
      : {};
  const isCurrentShowcase =
    loadedShowcase?.flowToken === flowToken &&
    loadedShowcase.autoOpenResourceId === autoOpenResourceId;
  const currentLoadError =
    loadError?.flowToken === flowToken &&
    loadError.autoOpenResourceId === autoOpenResourceId
      ? loadError.message
      : null;
  const flow = isCurrentShowcase ? loadedShowcase.flow : null;
  const resources = isCurrentShowcase ? loadedShowcase.resources : [];
  const explicitVerificationRequired = isCurrentShowcase
    ? loadedShowcase.explicitVerificationRequired
    : false;
  const polled = usePreCheckPolling(flowToken, 5000, {
    enabled: Boolean(flow),
    relevantResourceIds: resources.map((resource) => resource.id),
    pollNowKey: preCheckPollNowKey,
    resetResourceId: preCheckReset.resourceId,
    resetResourceKey: preCheckReset.key,
    allowMemberRegression: explicitVerificationRequired,
    stopWhenAllTerminal: !explicitVerificationRequired,
  });
  const customCss = flow?.custom_css?.trim() ? flow.custom_css : null;
  const pendingDeliveries =
    pendingDeliveryState.flowToken === flowToken
      ? pendingDeliveryState.deliveries
      : {};
  const completedDeliveryResourceIds = isCurrentShowcase
    ? loadedShowcase.completedDeliveryResourceIds
    : [];
  const orphanPendingResourceIds = Object.keys(pendingDeliveries)
    .map(Number)
    .filter(
      (resourceId) => !resources.some((resource) => resource.id === resourceId),
    );

  useEffect(() => {
    const nextState = {
      flowToken,
      deliveries: readPendingDeliveries(flowToken),
    };
    pendingDeliveryRef.current = nextState;
    setPendingDeliveryState(nextState);
  }, [flowToken]);

  useEffect(() => {
    finishExplicitVerifyRetryWaiters();
    setAutoOpenText(autoOpenResourceId ? AUTO_OPEN_LOADING_TEXT : null);
    setAutoOpenRetryable(false);
    autoOpenAttempted.current = false;
    explicitAutoOpenClickRetryResourceId.current = null;
    autoVerifyAttempted.current = false;
    autoVerifyArmed.current = false;
    autoVerifyCleanup.current?.();
    autoVerifyCleanup.current = null;
  }, [flowToken, autoOpenResourceId]);

  useEffect(() => {
    resourceActionsMounted.current = true;
    return () => {
      resourceActionsMounted.current = false;
      finishExplicitVerifyRetryWaiters();
      latestResourceActionEpochs.current.clear();
      busyResourceActions.current.clear();
      autoVerifyCleanup.current?.();
    };
  }, []);

  useEffect(() => {
    if (!explicitVerificationRequired) return;
    autoVerifyArmed.current = false;
    autoVerifyCleanup.current?.();
    autoVerifyCleanup.current = null;
  }, [explicitVerificationRequired]);

  useEffect(() => {
    let ignored = false;
    const controller = new AbortController();
    setLoadedShowcase(null);
    setLoadError(null);
    setLocal({});
    resourceActionMessagesContext.current = {
      flowToken,
      autoOpenResourceId,
    };
    setResourceActionMessages({});
    busyResourceActions.current.clear();
    latestResourceActionEpochs.current.clear();
    alreadyOpenedHintRequests.current.clear();
    setBusyResourceIds(new Set());
    void (async () => {
      try {
        const r = await userApi.getShowcase(
          flowToken,
          autoOpenResourceId ? "resource_auto_open" : "showcase",
          controller.signal,
        );
        if (ignored) return;
        if (r.error || !r.data) {
          setLoadError({
            flowToken,
            autoOpenResourceId,
            message: errorMessage(r.error, SHOWCASE_LOAD_ERROR_TEXT),
          });
          return;
        }
        const responseData = r.data;
        const nextResources = responseData.resources;
        const systemResource = autoOpenResourceId
          ? null
          : (responseData.system_resource ?? null);
        rememberServerPendingDeliveries(
          responseData.pending_delivery_resource_ids ?? [],
        );
        setLoadedShowcase({
          flowToken,
          autoOpenResourceId,
          flow: responseData.flow,
          resources: nextResources,
          systemResource,
          systemResourceCompleted: readSystemResourceCompleted(
            flowToken,
            systemResource,
            completedSystemResourceKey,
          ),
          systemResourceOpenFailed: false,
          completedDeliveryResourceIds: (
            responseData.completed_delivery_resource_ids ?? []
          ).filter(
            (resourceId): resourceId is number =>
              Number.isSafeInteger(resourceId) && resourceId > 0,
          ),
          explicitVerificationRequired:
            responseData.explicit_verification_required === true,
        });
      } catch {
        if (ignored || controller.signal.aborted) return;
        setLoadError({
          flowToken,
          autoOpenResourceId,
          message: SHOWCASE_LOAD_ERROR_TEXT,
        });
      }
    })();
    return () => {
      ignored = true;
      controller.abort();
    };
  }, [flowToken, autoOpenResourceId, loadAttempt]);

  useEffect(() => {
    if (!isCurrentShowcase || !explicitVerificationRequired) {
      return;
    }

    let disposed = false;
    let wasHidden = document.visibilityState === "hidden";
    let timer: number | undefined;
    let scheduledFor = 0;
    let requestInFlight = false;
    let requestQueued = false;
    let requestController: AbortController | undefined;
    let lifecycleEpoch = 0;
    let nextAllowedAt = 0;
    let backpressureUntil = 0;
    let backpressureFailures = 0;
    let automaticRetryAvailable = false;
    const webApp = tgWebApp();
    let telegramActive = webApp?.isActive !== false;

    function clearReconciliationTimer() {
      if (timer === undefined) return;
      window.clearTimeout(timer);
      timer = undefined;
      scheduledFor = 0;
    }

    function invalidateActiveRequest() {
      lifecycleEpoch += 1;
      requestController?.abort();
    }

    function scheduleReconciliation() {
      if (
        disposed ||
        document.visibilityState === "hidden" ||
        !telegramActive
      ) {
        return;
      }
      const now = Date.now();
      const target = Math.max(
        now + SHOWCASE_RECONCILE_DEBOUNCE_MS,
        nextAllowedAt,
      );
      if (timer !== undefined && scheduledFor <= target) return;
      clearReconciliationTimer();
      scheduledFor = target;
      timer = window.setTimeout(
        () => {
          timer = undefined;
          scheduledFor = 0;
          void reconcileShowcase();
        },
        Math.max(0, target - now),
      );
    }

    function requestReconciliation(urgent = false) {
      automaticRetryAvailable = true;
      if (urgent) {
        nextAllowedAt = Math.min(
          nextAllowedAt,
          Math.max(Date.now(), backpressureUntil),
        );
      }
      if (requestInFlight) {
        requestQueued = true;
        return;
      }
      scheduleReconciliation();
    }

    function requestUrgentReconciliation() {
      requestReconciliation(true);
    }

    function requestReconciliationAfterAction() {
      reconciliationRequestedAfterAction.current = true;
      if (busyResourceActions.current.size === 0) {
        reconciliationRequestedAfterAction.current = false;
        requestUrgentReconciliation();
      }
    }

    async function reconcileShowcase() {
      if (
        disposed ||
        document.visibilityState === "hidden" ||
        !telegramActive
      ) {
        return;
      }
      if (requestInFlight) {
        requestQueued = true;
        return;
      }
      if (busyResourceActions.current.size > 0) {
        reconciliationRequestedAfterAction.current = true;
        return;
      }
      if (Date.now() < nextAllowedAt) {
        scheduleReconciliation();
        return;
      }

      const actionGenerationAtStart = resourceActionGeneration.current;
      const requestLifecycleEpoch = lifecycleEpoch;
      const controller = new AbortController();
      nextAllowedAt = Date.now() + SHOWCASE_RECONCILE_MIN_INTERVAL_MS;
      requestInFlight = true;
      requestController = controller;
      try {
        const result = await userApi.getShowcase(
          flowToken,
          autoOpenResourceId ? "resource_auto_open" : "showcase",
          controller.signal,
        );
        const currentContext = reconciliationContext.current;
        if (
          disposed ||
          controller.signal.aborted ||
          requestLifecycleEpoch !== lifecycleEpoch ||
          currentContext.flowToken !== flowToken ||
          currentContext.autoOpenResourceId !== autoOpenResourceId
        ) {
          return;
        }
        if (
          resourceActionGeneration.current !== actionGenerationAtStart ||
          busyResourceActions.current.size > 0
        ) {
          requestReconciliationAfterAction();
          return;
        }
        if (result.error || !result.data) {
          const backoffMs = showcaseBackpressureDelayMs(
            result.response,
            ++backpressureFailures,
          );
          if (backoffMs !== null) {
            backpressureUntil = Date.now() + backoffMs;
            nextAllowedAt = Math.max(nextAllowedAt, backpressureUntil);
            if (automaticRetryAvailable) {
              automaticRetryAvailable = false;
              requestQueued = true;
            }
          } else {
            backpressureUntil = 0;
            backpressureFailures = 0;
          }
          return;
        }
        backpressureUntil = 0;
        backpressureFailures = 0;
        automaticRetryAvailable = false;

        const responseData = result.data;
        const nextResources = responseData.resources;
        const systemResource = autoOpenResourceId
          ? null
          : (responseData.system_resource ?? null);
        rememberServerPendingDeliveries(
          responseData.pending_delivery_resource_ids ?? [],
        );
        setLoadedShowcase((current) => {
          if (
            current?.flowToken !== flowToken ||
            current.autoOpenResourceId !== autoOpenResourceId
          ) {
            return current;
          }
          return {
            flowToken,
            autoOpenResourceId,
            flow: responseData.flow,
            resources: nextResources,
            systemResource,
            systemResourceCompleted:
              systemResource?.revision === current.systemResource?.revision
                ? current.systemResourceCompleted
                : readSystemResourceCompleted(
                    flowToken,
                    systemResource,
                    completedSystemResourceKey,
                  ),
            systemResourceOpenFailed:
              systemResource?.revision === current.systemResource?.revision &&
              current.systemResourceOpenFailed,
            completedDeliveryResourceIds: (
              responseData.completed_delivery_resource_ids ?? []
            ).filter(
              (resourceId): resourceId is number =>
                Number.isInteger(resourceId) && resourceId > 0,
            ),
            explicitVerificationRequired:
              responseData.explicit_verification_required === true,
          };
        });
        setLocal({});
        const reconciledMessages: Record<number, ResourceActionMessage> = {};
        for (const [resourceId, epoch] of alreadyOpenedHintRequests.current) {
          const resource = nextResources.find((item) => item.id === resourceId);
          if (
            resource?.initial_status === "pending" &&
            latestResourceActionEpochs.current.get(resourceId) === epoch
          ) {
            reconciledMessages[resourceId] = {
              epoch,
              tone: "hint",
              text: ALREADY_OPENED_HINT,
            };
          }
        }
        alreadyOpenedHintRequests.current.clear();
        setResourceActionMessages(reconciledMessages);
      } catch {
        // Keep the last successful snapshot and local state on transient failures.
        const currentContext = reconciliationContext.current;
        if (
          !disposed &&
          !controller.signal.aborted &&
          requestLifecycleEpoch === lifecycleEpoch &&
          currentContext.flowToken === flowToken &&
          currentContext.autoOpenResourceId === autoOpenResourceId &&
          (resourceActionGeneration.current !== actionGenerationAtStart ||
            busyResourceActions.current.size > 0)
        ) {
          requestReconciliationAfterAction();
        }
      } finally {
        if (!disposed) {
          if (requestController === controller) requestController = undefined;
          requestInFlight = false;
          if (requestQueued) {
            requestQueued = false;
            scheduleReconciliation();
          }
        }
      }
    }

    function reconcileAfterReturn() {
      if (!wasHidden || document.visibilityState === "hidden") return;
      wasHidden = false;
      requestReconciliation();
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "hidden") {
        wasHidden = true;
        clearReconciliationTimer();
        invalidateActiveRequest();
        return;
      }
      reconcileAfterReturn();
    }

    function handleFocus() {
      if (document.visibilityState === "hidden") {
        wasHidden = true;
        return;
      }
      wasHidden = false;
      requestReconciliation();
    }

    function handleTelegramActivated() {
      telegramActive = true;
      lifecycleEpoch += 1;
      if (document.visibilityState !== "hidden") requestReconciliation(true);
    }

    function handleTelegramDeactivated() {
      telegramActive = false;
      clearReconciliationTimer();
      invalidateActiveRequest();
    }

    scheduleShowcaseReconciliation.current = requestUrgentReconciliation;
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    webApp?.onEvent?.("activated", handleTelegramActivated);
    webApp?.onEvent?.("deactivated", handleTelegramDeactivated);
    return () => {
      disposed = true;
      requestQueued = false;
      clearReconciliationTimer();
      invalidateActiveRequest();
      if (
        scheduleShowcaseReconciliation.current === requestUrgentReconciliation
      ) {
        scheduleShowcaseReconciliation.current = null;
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      webApp?.offEvent?.("activated", handleTelegramActivated);
      webApp?.offEvent?.("deactivated", handleTelegramDeactivated);
    };
  }, [
    autoOpenResourceId,
    explicitVerificationRequired,
    flowToken,
    isCurrentShowcase,
  ]);

  function retryLoad() {
    setLoadError(null);
    setLoadAttempt((attempt) => attempt + 1);
  }

  // Финальный статус каждого ресурса = durable polling → local override →
  // persistent backend status → default. NO_CHECK ресурсы доступны сразу: pre-check
  // может вернуть pending до фоновой инициализации, но проверять там нечего.
  const statuses: Record<number, PreCheckStatus> = Object.fromEntries(
    resources.map((r) => [
      r.id,
      r.verification_mode === "no_check" && pendingDeliveries[String(r.id)]
        ? "not_member"
        : statusForResource(r, local, polled, explicitVerificationRequired),
    ]),
  );

  const availableResources = resources.filter(
    (r) => statuses[r.id] !== "system_bot_revoked",
  );
  const completedOrphanResourceIds = completedDeliveryResourceIds.filter(
    (resourceId) =>
      !resources.some((resource) => resource.id === resourceId) &&
      !pendingDeliveries[String(resourceId)],
  );
  const visibleResources = availableResources.filter((r) => {
    const status = statuses[r.id];
    return status !== "member";
  });
  const systemResource =
    isCurrentShowcase && visibleResources.length >= 2
      ? loadedShowcase.systemResource
      : null;
  if (systemResourceAnchor.current?.flowToken !== flowToken) {
    systemResourceAnchor.current = null;
  }
  if (
    systemResource &&
    systemResourceAnchor.current?.revision !== systemResource.revision
  ) {
    // Запоминаем соседей при первом показе: выполненные задания перед
    // карточкой не должны сдвигать её за следующие обычные ресурсы.
    systemResourceAnchor.current = {
      flowToken,
      revision: systemResource.revision,
      precedingResourceIds: new Set(
        [
          ...visibleResources.map((resource) => resource.id),
          ...orphanPendingResourceIds,
        ].slice(0, systemResource.position - 1),
      ),
    };
  }
  const systemResourceCompleted =
    Boolean(systemResource) && loadedShowcase?.systemResourceCompleted === true;
  const systemResourceOpenFailed =
    Boolean(systemResource) &&
    loadedShowcase?.systemResourceOpenFailed === true;
  const completed =
    availableResources.filter((r) => statuses[r.id] === "member").length +
    completedOrphanResourceIds.length +
    Number(systemResourceCompleted);
  const total =
    availableResources.length +
    orphanPendingResourceIds.length +
    completedOrphanResourceIds.length +
    Number(Boolean(systemResource));
  // Пустой с самого начала flow не выдаёт доступ. Но если GET /showcase
  // действительно назначил задания, а затем authoritative pre-check исключил
  // их все, исчезнувшие задания больше не должны держать финальную кнопку.
  if (assignedTasksObserved.current.flowToken !== flowToken) {
    assignedTasksObserved.current = { flowToken, observed: false };
  }
  if (
    resources.length > 0 ||
    orphanPendingResourceIds.length > 0 ||
    completedOrphanResourceIds.length > 0
  ) {
    assignedTasksObserved.current.observed = true;
  }
  const allDone = assignedTasksObserved.current.observed && completed === total;
  const returnUrl = flow ? normalizeReturnLink(flow.success_url) : null;
  // Кнопку показываем только когда есть куда вести. Раньше хватало непустой
  // строки, и нераспознанная ссылка давала вечный замок: все каналы пройдены,
  // прогресс 100%, а кнопка не нажимается и выглядит как «условия не выполнены».
  const hasFinalButton = Boolean(returnUrl);
  const finalReady = allDone && Boolean(returnUrl);
  const visibleTaskCount =
    visibleResources.length +
    orphanPendingResourceIds.length +
    Number(Boolean(systemResource) && !systemResourceCompleted);

  function openSystemResource() {
    if (!systemResource || systemResourceCompleted) return;
    let openFailed = false;
    try {
      openTelegramLink(systemResource.url);
    } catch {
      openFailed = true;
    }
    if (!openFailed) {
      const key = systemResourceStorageKey(flowToken, systemResource.revision);
      completedSystemResourceKey.current = key;
      try {
        window.sessionStorage.setItem(key, "1");
      } catch {
        // Текущий mount сохраняет выполнение и без browser storage.
      }
    }
    setLoadedShowcase((current) =>
      current?.flowToken === flowToken &&
      current.systemResource?.revision === systemResource.revision
        ? {
            ...current,
            systemResourceCompleted: !openFailed,
            systemResourceOpenFailed: openFailed,
          }
        : current,
    );
  }

  function rememberPendingDelivery(
    resourceId: number,
    token: string,
    expiresAt: string,
  ) {
    const current =
      pendingDeliveryRef.current.flowToken === flowToken
        ? pendingDeliveryRef.current.deliveries
        : {};
    const deliveries = {
      ...current,
      [resourceId]: {
        token,
        expiresAt,
        createdAt: current[String(resourceId)]?.createdAt ?? Date.now(),
      },
    };
    const nextState = { flowToken, deliveries };
    // Memory is authoritative for the current mount. Storage is only durable
    // best-effort and may throw in Telegram privacy modes.
    pendingDeliveryRef.current = nextState;
    setPendingDeliveryState(nextState);
    writePendingDeliveries(flowToken, deliveries);
  }

  function rememberPendingClick(resourceId: number): PendingDelivery {
    const current =
      pendingDeliveryRef.current.flowToken === flowToken
        ? pendingDeliveryRef.current.deliveries
        : {};
    const existing = current[String(resourceId)];
    if (existing) return existing;
    const pending: PendingDelivery = {
      token: null,
      expiresAt: null,
      createdAt: Date.now(),
    };
    const deliveries = { ...current, [resourceId]: pending };
    const nextState = { flowToken, deliveries };
    pendingDeliveryRef.current = nextState;
    setPendingDeliveryState(nextState);
    writePendingDeliveries(flowToken, deliveries);
    return pending;
  }

  function rememberServerPendingDeliveries(resourceIds: readonly number[]) {
    const current =
      pendingDeliveryRef.current.flowToken === flowToken
        ? pendingDeliveryRef.current.deliveries
        : {};
    let changed = false;
    const deliveries = { ...current };
    for (const resourceId of resourceIds) {
      if (!Number.isSafeInteger(resourceId) || resourceId <= 0) continue;
      if (deliveries[String(resourceId)]) continue;
      deliveries[String(resourceId)] = {
        token: null,
        expiresAt: null,
        createdAt: Date.now(),
      };
      changed = true;
    }
    if (!changed) return;
    const nextState = { flowToken, deliveries };
    pendingDeliveryRef.current = nextState;
    setPendingDeliveryState(nextState);
    writePendingDeliveries(flowToken, deliveries);
  }

  function markPendingAcknowledgement(resourceId: number, token: string) {
    const current =
      pendingDeliveryRef.current.flowToken === flowToken
        ? pendingDeliveryRef.current.deliveries
        : {};
    const pending = current[String(resourceId)];
    if (!pending || pending.token !== token) return;
    const deliveries = {
      ...current,
      [resourceId]: { ...pending, acknowledgementPending: true },
    };
    const nextState = { flowToken, deliveries };
    pendingDeliveryRef.current = nextState;
    setPendingDeliveryState(nextState);
    writePendingDeliveries(flowToken, deliveries);
  }

  function forgetPendingDelivery(resourceId: number) {
    const current =
      pendingDeliveryRef.current.flowToken === flowToken
        ? pendingDeliveryRef.current.deliveries
        : {};
    const deliveries = { ...current };
    delete deliveries[String(resourceId)];
    const nextState = { flowToken, deliveries };
    pendingDeliveryRef.current = nextState;
    setPendingDeliveryState(nextState);
    writePendingDeliveries(flowToken, deliveries);
  }

  function currentPendingDelivery(resourceId: number): PendingDelivery | null {
    if (pendingDeliveryRef.current.flowToken !== flowToken) return null;
    return pendingDeliveryRef.current.deliveries[String(resourceId)] ?? null;
  }

  function reconcileUnavailableResource(resourceId: number) {
    // 409 resource_unavailable авторитетнее старого polling snapshot. Убираем
    // карточку синхронно, затем немедленно перезапускаем pre-check, чтобы одним
    // полным snapshot сверить и остальные задания flow.
    setLocal((prev) => ({
      ...prev,
      [resourceId]: "system_bot_revoked",
    }));
    forgetPendingDelivery(resourceId);
    alreadyOpenedHintRequests.current.delete(resourceId);
    clearResourceActionMessage(resourceId);
    pollPreCheckNow();
  }

  function pollPreCheckNow() {
    setPreCheckPollNowKey((key) => key + 1);
  }

  function resetPreCheckResource(resourceId: number) {
    setPreCheckReset((current) => ({
      resourceId,
      key: current.key + 1,
    }));
    setPreCheckPollNowKey((key) => key + 1);
  }

  function clearResourceActionMessage(resourceId: number) {
    setResourceActionMessages((current) => {
      if (!(resourceId in current)) return current;
      const next = { ...current };
      delete next[resourceId];
      return next;
    });
  }

  function setResourceActionMessage(
    resourceId: number,
    epoch: number,
    text: string,
    tone: ResourceActionMessage["tone"] = "error",
  ) {
    if (latestResourceActionEpochs.current.get(resourceId) !== epoch) return;
    setResourceActionMessages((current) => ({
      ...current,
      [resourceId]: { epoch, tone, text },
    }));
  }

  function beginResourceAction(resourceId: number): number | null {
    if (busyResourceActions.current.has(resourceId)) return null;
    const token = ++nextResourceActionToken.current;
    resourceActionGeneration.current += 1;
    latestResourceActionEpochs.current.set(resourceId, token);
    alreadyOpenedHintRequests.current.delete(resourceId);
    clearResourceActionMessage(resourceId);
    busyResourceActions.current.set(resourceId, token);
    setBusyResourceIds(new Set(busyResourceActions.current.keys()));
    return token;
  }

  function finishResourceAction(resourceId: number, token: number): void {
    if (busyResourceActions.current.get(resourceId) !== token) return;
    busyResourceActions.current.delete(resourceId);
    resourceActionGeneration.current += 1;
    setBusyResourceIds(new Set(busyResourceActions.current.keys()));
    if (
      busyResourceActions.current.size === 0 &&
      reconciliationRequestedAfterAction.current
    ) {
      reconciliationRequestedAfterAction.current = false;
      scheduleShowcaseReconciliation.current?.();
    }
  }

  // Юзер нажал «открыть»/«подписаться»: фиксируем клик на бэке, открываем
  // deep-link на ресурс.
  // - NO_CHECK: completed=true означает, что conversion/charge уже приняты,
  //   но задание завершаем только после успешной доставки и открытия ссылки.
  // - VERIFIED: completed=false → открываем для подписки, потом юзер нажмёт verify.
  async function onClick(
    r: ShowcaseResource | null,
    pendingResourceId?: number,
  ): Promise<ClickOutcome> {
    const resourceId = r?.id ?? pendingResourceId;
    if (resourceId === undefined) return "unavailable";
    const actionToken = beginResourceAction(resourceId);
    if (actionToken === null) return "retryable";
    const actionFlowToken = flowToken;
    const actionAutoOpenResourceId = autoOpenResourceId;
    const actionIsCurrent = () => {
      const context = reconciliationContext.current;
      return (
        resourceActionsMounted.current &&
        latestResourceActionEpochs.current.get(resourceId) === actionToken &&
        context.flowToken === actionFlowToken &&
        context.autoOpenResourceId === actionAutoOpenResourceId
      );
    };
    try {
      let pending = currentPendingDelivery(resourceId);
      const wasPendingBeforeRequest = Boolean(pending);
      if (!pending && r?.verification_mode === "no_check") {
        // Persist intent *before* POST. If the server commits the paid click but
        // its HTTP response is lost, member polling/auto-pause cannot hide the
        // retry action or unlock final access before URL delivery.
        pending = rememberPendingClick(resourceId);
        setLocal((prev) => ({ ...prev, [resourceId]: "not_member" }));
      }
      let retryRedirectToken: string | undefined;
      if (pending?.token) {
        if (pending.acknowledgementPending) {
          const acknowledged = await acknowledgeDelivery(
            resourceId,
            pending.token,
          );
          if (!actionIsCurrent()) return "stale";
          if (!acknowledged) {
            setResourceActionMessage(
              resourceId,
              actionToken,
              "Переход открыт, но подтверждение не сохранено. Попробуйте ещё раз.",
            );
            return "retryable";
          }
          forgetPendingDelivery(resourceId);
          setLocal((prev) => setLocalStatus(prev, resourceId, "member"));
          pollPreCheckNow();
          return "completed";
        }
        const pendingResolve = await userApi.resolveRedirectToken(
          pending.token,
        );
        if (!actionIsCurrent()) return "stale";
        if (pendingResolve.data?.invite_url) {
          openTelegramLink(pendingResolve.data.invite_url);
          markPendingAcknowledgement(resourceId, pending.token);
          const acknowledged = await acknowledgeDelivery(
            resourceId,
            pending.token,
          );
          if (!actionIsCurrent()) return "stale";
          if (!acknowledged) {
            setResourceActionMessage(
              resourceId,
              actionToken,
              "Переход открыт, но подтверждение не сохранено. Попробуйте ещё раз.",
            );
            return "retryable";
          }
          forgetPendingDelivery(resourceId);
          setLocal((prev) => setLocalStatus(prev, resourceId, "member"));
          pollPreCheckNow();
          return "opened";
        }
        if (errorDetail(pendingResolve.error) !== "redirect_token_gone") {
          // A transport/5xx/lock failure is ambiguous.  Keep the exact unused
          // token and never issue another click until the server explicitly
          // says that this token is gone.
          setResourceActionMessage(
            resourceId,
            actionToken,
            resourceActionFailureMessage(
              pendingResolve.error,
              pendingResolve.response,
              "resolve",
            ),
          );
          return "retryable";
        }
        retryRedirectToken = pending.token;
      }

      let replacementAttempted = Boolean(retryRedirectToken);
      while (true) {
        const resp = await userApi.click({
          flow_token: flowToken,
          resource_id: resourceId,
          ...(wasPendingBeforeRequest ||
          (r?.verification_mode === "no_check" && retryRedirectToken)
            ? { delivery_recovery: true }
            : {}),
          ...(retryRedirectToken
            ? { retry_redirect_token: retryRedirectToken }
            : {}),
        });
        if (!actionIsCurrent()) return "stale";
        if (resp.error) {
          const detail = errorDetail(resp.error);
          if (detail === "verification_temporarily_unavailable") {
            const message = verificationTemporarilyUnavailableMessage(
              resp.response,
            );
            setResourceActionMessage(resourceId, actionToken, message);
            if (isAutoOpenMode) setAutoOpenText(message);
            return "verification_temporarily_unavailable";
          }
          if (detail === "resource_already_completed") {
            if (wasPendingBeforeRequest && currentPendingDelivery(resourceId)) {
              setResourceActionMessage(
                resourceId,
                actionToken,
                "Переход уже открыт. Завершите его или нажмите кнопку ещё раз.",
                "hint",
              );
              return "retryable";
            }
            if (
              explicitVerificationRequired &&
              r?.verification_mode === "verified"
            ) {
              alreadyOpenedHintRequests.current.set(resourceId, actionToken);
              reconciliationRequestedAfterAction.current = true;
              return "verification_required";
            }
            forgetPendingDelivery(resourceId);
            setLocal((prev) => setLocalStatus(prev, resourceId, "member"));
            return "completed";
          }
          const noCheckDelivery =
            r?.verification_mode === "no_check" || Boolean(pending);
          const terminalUnavailable =
            detail === "resource_unavailable" ||
            (noCheckDelivery &&
              (detail === "resource_inactive" ||
                detail === "period_not_active" ||
                detail === "insufficient_balance" ||
                detail === "flow_token_invalid"));
          if (terminalUnavailable) {
            reconcileUnavailableResource(resourceId);
            return "unavailable";
          }
          setResourceActionMessage(
            resourceId,
            actionToken,
            resourceActionFailureMessage(resp.error, resp.response, "click"),
          );
          return "retryable";
        }

        const deliveryPending = Boolean(
          resp.data?.completed &&
          (r?.verification_mode === "no_check" || pending),
        );
        if (
          deliveryPending &&
          resp.data?.redirect_token &&
          resp.data.expires_at
        ) {
          rememberPendingDelivery(
            resourceId,
            resp.data.redirect_token,
            resp.data.expires_at,
          );
          setLocal((prev) => ({ ...prev, [resourceId]: "not_member" }));
        }
        if (resp.data?.redirect_token) {
          const redirectToken = resp.data.redirect_token;
          const resolved = await userApi.resolveRedirectToken(redirectToken);
          if (!actionIsCurrent()) return "stale";
          if (resolved.data?.invite_url) {
            openTelegramLink(resolved.data.invite_url);
            if (deliveryPending) {
              markPendingAcknowledgement(resourceId, redirectToken);
              const acknowledged = await acknowledgeDelivery(
                resourceId,
                redirectToken,
              );
              if (!actionIsCurrent()) return "stale";
              if (!acknowledged) {
                setResourceActionMessage(
                  resourceId,
                  actionToken,
                  "Переход открыт, но подтверждение не сохранено. Попробуйте ещё раз.",
                );
                return "retryable";
              }
              forgetPendingDelivery(resourceId);
              setLocal((prev) => setLocalStatus(prev, resourceId, "member"));
              pollPreCheckNow();
            }
            if (r?.type === "bot" && r.verification_mode === "verified") {
              armBotAutoVerify(r);
            }
            return "opened";
          }
          if (
            errorDetail(resolved.error) === "redirect_token_gone" &&
            !replacementAttempted
          ) {
            replacementAttempted = true;
            retryRedirectToken =
              r?.verification_mode === "no_check" || r === null
                ? redirectToken
                : undefined;
            continue;
          }
          setResourceActionMessage(
            resourceId,
            actionToken,
            resourceActionFailureMessage(
              resolved.error,
              resolved.response,
              "resolve",
            ),
          );
          return "retryable";
        }
        setResourceActionMessage(
          resourceId,
          actionToken,
          "Ссылка перехода не получена. Нажмите «открыть» ещё раз.",
        );
        return "retryable";
      }
    } catch {
      if (!actionIsCurrent()) return "stale";
      setResourceActionMessage(
        resourceId,
        actionToken,
        "Нет соединения. Проверьте интернет и повторите попытку.",
      );
      return "retryable";
    } finally {
      finishResourceAction(resourceId, actionToken);
    }
  }

  async function onVerify(
    r: ShowcaseResource,
    options: { silent?: boolean } = {},
  ): Promise<boolean> {
    const actionToken = beginResourceAction(r.id);
    if (actionToken === null) return false;
    const actionFlowToken = flowToken;
    const actionAutoOpenResourceId = autoOpenResourceId;
    const actionIsCurrent = () => {
      const context = reconciliationContext.current;
      return (
        resourceActionsMounted.current &&
        latestResourceActionEpochs.current.get(r.id) === actionToken &&
        context.flowToken === actionFlowToken &&
        context.autoOpenResourceId === actionAutoOpenResourceId
      );
    };
    try {
      for (
        let attempt = 0;
        attempt < EXPLICIT_VERIFY_MAX_ATTEMPTS;
        attempt += 1
      ) {
        const resp = await userApi.verify({
          flow_token: flowToken,
          resource_id: r.id,
          ...(explicitVerificationRequired
            ? { confirmation_source: "checkbox" as const }
            : {}),
        });
        if (!actionIsCurrent()) return false;
        if (resp.error) {
          const unavailable =
            errorDetail(resp.error) === "resource_unavailable";
          if (unavailable) {
            reconcileUnavailableResource(r.id);
          } else {
            setLocal((prev) =>
              setLocalStatus(
                prev,
                r.id,
                explicitVerificationRequired ? "pending" : "error",
              ),
            );
          }
          if (!options.silent && !unavailable) {
            setResourceActionMessage(
              r.id,
              actionToken,
              (explicitVerificationRequired
                ? verifyRetryMessage(undefined, resp.response)
                : null) ??
                resourceActionFailureMessage(
                  resp.error,
                  resp.response,
                  "verify",
                ),
              explicitVerificationRequired ? "hint" : "error",
            );
          }
          return false;
        }
        const result = normalizeVerifyResult(resp.data);
        setLocal((prev) => setLocalStatus(prev, r.id, result.status));
        if (result.verified && explicitVerificationRequired) {
          // A successful repeat completion supersedes an older durable leave.
          // Drop that polling snapshot and immediately read the new DB state.
          resetPreCheckResource(r.id);
        }
        if (result.verified) {
          tgWebApp()?.HapticFeedback?.notificationOccurred?.("success");
          return true;
        }

        if (explicitVerificationRequired && awaitsDurableProof(resp.data)) {
          // Telegram webhook / durable BOT jobs complete through DB state.
          // A second provider verify would only spend the rate limit again.
          pollPreCheckNow();
          if (!options.silent) {
            const message = verifyRetryMessage(resp.data, resp.response);
            if (message) {
              setResourceActionMessage(r.id, actionToken, message, "hint");
            }
          }
          return false;
        }

        const retryDelayMs = explicitVerificationRequired
          ? explicitVerifyRetryDelayMs(resp.data, resp.response)
          : null;
        if (
          retryDelayMs !== null &&
          attempt + 1 < EXPLICIT_VERIFY_MAX_ATTEMPTS
        ) {
          if (!options.silent) {
            setResourceActionMessage(
              r.id,
              actionToken,
              verifyRetryMessage(resp.data, resp.response) ??
                "Проверяем выполнение задания…",
              "hint",
            );
          }
          await waitForExplicitVerifyRetry(retryDelayMs);
          if (!actionIsCurrent()) return false;
          continue;
        }

        if (
          explicitVerificationRequired &&
          requestsAutomaticFollowup(resp.data)
        ) {
          pollPreCheckNow();
        }
        if (explicitVerificationRequired && !options.silent) {
          const retryMessage = verifyRetryMessage(resp.data, resp.response);
          if (retryMessage) {
            setResourceActionMessage(r.id, actionToken, retryMessage, "hint");
          }
        }
        return false;
      }
      return false;
    } catch {
      if (!actionIsCurrent()) return false;
      setLocal((prev) => setLocalStatus(prev, r.id, "error"));
      if (!options.silent) {
        setResourceActionMessage(
          r.id,
          actionToken,
          "Нет соединения. Проверьте интернет и повторите попытку.",
        );
      }
      return false;
    } finally {
      finishResourceAction(r.id, actionToken);
    }
  }

  function waitForExplicitVerifyRetry(delayMs: number): Promise<void> {
    return new Promise((resolve) => {
      let timer: number | undefined;
      const finishWait = () => {
        if (timer === undefined) return;
        window.clearTimeout(timer);
        timer = undefined;
        explicitVerifyRetryWaiters.current.delete(finishWait);
        resolve();
      };
      timer = window.setTimeout(finishWait, delayMs);
      explicitVerifyRetryWaiters.current.add(finishWait);
    });
  }

  function finishExplicitVerifyRetryWaiters(): void {
    for (const finishWait of explicitVerifyRetryWaiters.current) {
      finishWait();
    }
    explicitVerifyRetryWaiters.current.clear();
  }

  async function acknowledgeDelivery(
    resourceId: number,
    redirectToken: string,
  ): Promise<boolean> {
    try {
      const response = await userApi.acknowledgeDelivery({
        flow_token: flowToken,
        resource_id: resourceId,
        redirect_token: redirectToken,
      });
      return response.data?.acknowledged === true;
    } catch {
      return false;
    }
  }

  function armBotAutoVerify(resource: ShowcaseResource) {
    if (explicitVerificationRequired) return;
    if (autoVerifyAttempted.current || autoVerifyArmed.current) return;
    autoVerifyArmed.current = true;

    let timer: number | undefined;

    async function verifyOnce() {
      if (autoVerifyAttempted.current) return;
      autoVerifyAttempted.current = true;
      autoVerifyArmed.current = false;
      cleanup();
      autoVerifyCleanup.current = null;
      await onVerify(resource, { silent: true });
    }

    function run() {
      void verifyOnce();
    }

    function onVisibilityChange() {
      if (document.visibilityState === "visible") run();
    }

    function cleanup() {
      window.removeEventListener("focus", run);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (timer !== undefined) window.clearTimeout(timer);
    }

    window.addEventListener("focus", run);
    document.addEventListener("visibilitychange", onVisibilityChange);
    timer = window.setTimeout(run, 8000);
    autoVerifyCleanup.current = cleanup;
  }

  function applyAutoOpenClickOutcome(
    resource: ShowcaseResource,
    outcome: ClickOutcome,
  ) {
    if (outcome === "stale") return;
    const retryable =
      outcome === "retryable" ||
      outcome === "verification_temporarily_unavailable";
    explicitAutoOpenClickRetryResourceId.current =
      explicitVerificationRequired &&
      resource.verification_mode === "verified" &&
      retryable
        ? resource.id
        : null;
    if (
      outcome === "opened" &&
      explicitVerificationRequired &&
      resource.verification_mode === "verified"
    ) {
      setAutoOpenRetryable(false);
      setAutoOpenText(AUTO_OPEN_VERIFY_REQUIRED_TEXT);
      return;
    }
    if (
      outcome === "opened" &&
      resource.type === "bot" &&
      resource.verification_mode === "verified"
    ) {
      setAutoOpenText(AUTO_OPEN_DONE_TEXT);
      armBotAutoVerify(resource);
      return;
    }
    setAutoOpenRetryable(retryable);
    if (outcome === "verification_temporarily_unavailable") return;
    setAutoOpenText(
      outcome === "opened"
        ? AUTO_OPEN_DONE_TEXT
        : outcome === "completed"
          ? AUTO_OPEN_COMPLETED_TEXT
          : outcome === "verification_required"
            ? AUTO_OPEN_VERIFY_REQUIRED_TEXT
            : AUTO_OPEN_UNAVAILABLE_TEXT,
    );
  }

  async function runAutoOpenClick(resource: ShowcaseResource) {
    applyAutoOpenClickOutcome(resource, await onClick(resource));
  }

  useEffect(() => {
    if (!autoOpenResourceId || autoOpenAttempted.current || !flow) {
      return;
    }
    const resource = resources.find((item) => item.id === autoOpenResourceId);
    if (!resource) {
      autoOpenAttempted.current = true;
      setAutoOpenRetryable(false);
      if (completedDeliveryResourceIds.includes(autoOpenResourceId)) {
        setAutoOpenText(AUTO_OPEN_COMPLETED_TEXT);
      } else if (currentPendingDelivery(autoOpenResourceId)) {
        void onClick(null, autoOpenResourceId).then((outcome) => {
          if (outcome === "stale") return;
          setAutoOpenRetryable(outcome === "retryable");
          setAutoOpenText(
            outcome === "opened"
              ? AUTO_OPEN_DONE_TEXT
              : outcome === "completed"
                ? AUTO_OPEN_COMPLETED_TEXT
                : AUTO_OPEN_UNAVAILABLE_TEXT,
          );
        });
      } else {
        setAutoOpenText(AUTO_OPEN_UNAVAILABLE_TEXT);
      }
      return;
    }
    const status = statuses[resource.id];
    if (status === "member") {
      autoOpenAttempted.current = true;
      setAutoOpenRetryable(false);
      setAutoOpenText(AUTO_OPEN_COMPLETED_TEXT);
      return;
    }
    if (status === "system_bot_revoked") {
      autoOpenAttempted.current = true;
      setAutoOpenRetryable(false);
      setAutoOpenText(AUTO_OPEN_UNAVAILABLE_TEXT);
      return;
    }
    if (resource.verification_mode === "verified") {
      if (status === "pending") {
        if (!explicitVerificationRequired) {
          setAutoOpenRetryable(false);
          setAutoOpenText(AUTO_OPEN_LOADING_TEXT);
          return;
        }
      }
      if (status === "error") {
        // Transient provider/cache failure is not a completed auto-open
        // attempt. A later polling status can recover automatically, while a
        // manual retry below reloads the current showcase snapshot.
        setAutoOpenRetryable(true);
        setAutoOpenText(AUTO_OPEN_UNAVAILABLE_TEXT);
        return;
      }
      if (
        status !== "not_member" &&
        !(explicitVerificationRequired && status === "pending")
      ) {
        autoOpenAttempted.current = true;
        setAutoOpenRetryable(false);
        setAutoOpenText(AUTO_OPEN_UNAVAILABLE_TEXT);
        return;
      }
    }
    autoOpenAttempted.current = true;
    setAutoOpenRetryable(false);
    void runAutoOpenClick(resource);
  }, [
    autoOpenResourceId,
    completedDeliveryResourceIds,
    flow,
    resources,
    statuses,
  ]);

  function retryAutoOpen() {
    setAutoOpenRetryable(false);
    setAutoOpenText(AUTO_OPEN_LOADING_TEXT);
    const explicitRetryResourceId =
      explicitAutoOpenClickRetryResourceId.current;
    explicitAutoOpenClickRetryResourceId.current = null;
    const explicitRetryResource = resources.find(
      (resource) =>
        resource.id === explicitRetryResourceId &&
        resource.verification_mode === "verified",
    );
    if (explicitVerificationRequired && explicitRetryResource) {
      autoOpenAttempted.current = true;
      void runAutoOpenClick(explicitRetryResource);
      return;
    }
    autoOpenAttempted.current = false;
    setLoadAttempt((attempt) => attempt + 1);
  }

  if (!flow) {
    if (currentLoadError) {
      return (
        <div className={isAutoOpenMode ? "tp-auto-open-overlay" : "tp-screen"}>
          <div className="tp-alert" role="alert">
            {currentLoadError}
          </div>
          <button
            type="button"
            className="tp-button-primary"
            onClick={retryLoad}
          >
            повторить
          </button>
        </div>
      );
    }
    if (isAutoOpenMode) {
      return <AutoOpenOverlay text={autoOpenText ?? AUTO_OPEN_LOADING_TEXT} />;
    }
    return (
      <div className="tp-screen" aria-busy="true">
        <div role="status">проверяем задания...</div>
      </div>
    );
  }

  if (isAutoOpenMode) {
    return (
      <AutoOpenOverlay
        text={autoOpenText ?? AUTO_OPEN_LOADING_TEXT}
        onRetry={autoOpenRetryable ? retryAutoOpen : undefined}
      />
    );
  }

  const resourceItems = [
    ...visibleResources.map((r) => (
      <li
        key={r.id}
        data-tp-part={SHOWCASE_DESIGN_PARTS.resourceItem}
        data-resource-type={r.type}
        data-verification-mode={r.verification_mode}
        data-owner={showcaseDesignResourceOwner(r.is_driver_own)}
        data-status={statuses[r.id]}
        data-busy={toShowcaseDesignBooleanState(busyResourceIds.has(r.id))}
      >
        <ResourceCard
          resource={r}
          status={statuses[r.id]}
          busy={busyResourceIds.has(r.id)}
          message={
            resourceActionMessages[r.id] ??
            defaultResourceStatusMessage(statuses[r.id])
          }
          onClick={() => void onClick(r)}
          onVerify={() => void onVerify(r)}
        />
      </li>
    )),
    ...orphanPendingResourceIds.map((resourceId) => (
      <li
        key={`pending-delivery-${resourceId}`}
        data-tp-part={SHOWCASE_DESIGN_PARTS.resourceItem}
        data-resource-type="unknown"
        data-verification-mode="no_check"
        data-owner="sponsor"
        data-status="not_member"
        data-busy={toShowcaseDesignBooleanState(
          busyResourceIds.has(resourceId),
        )}
      >
        <button
          type="button"
          className="tp-card tp-button-primary"
          disabled={busyResourceIds.has(resourceId)}
          aria-label={`повторить переход к ресурсу ${resourceId}`}
          onClick={() => void onClick(null, resourceId)}
        >
          {busyResourceIds.has(resourceId)
            ? "открываем..."
            : "повторить переход к ресурсу"}
        </button>
        {resourceActionMessages[resourceId] ? (
          <div
            className={
              resourceActionMessages[resourceId].tone === "error"
                ? "tp-alert"
                : "text-sm text-muted-foreground"
            }
            data-resource-message-tone={resourceActionMessages[resourceId].tone}
            role={
              resourceActionMessages[resourceId].tone === "error"
                ? "alert"
                : "status"
            }
          >
            {resourceActionMessages[resourceId].text}
          </div>
        ) : null}
      </li>
    )),
  ];
  if (systemResource && !systemResourceCompleted) {
    systemResourceIcon.current ??= randomShowcaseIcon();
    // Вставка выполняется после всех обычных фильтров и recovery-карточек.
    const precedingResourceIds =
      systemResourceAnchor.current!.precedingResourceIds;
    const insertionIndex =
      visibleResources.filter((resource) =>
        precedingResourceIds.has(resource.id),
      ).length +
      orphanPendingResourceIds.filter((id) => precedingResourceIds.has(id))
        .length;
    resourceItems.splice(
      insertionIndex,
      0,
      <li
        key={`system-resource-${systemResource.revision}`}
        data-tp-part={SHOWCASE_DESIGN_PARTS.resourceItem}
        data-resource-type="channel_public"
        data-verification-mode="no_check"
        data-owner="sponsor"
        data-status="not_member"
        data-busy="false"
      >
        <ResourceCard
          iconSrc={systemResourceIcon.current}
          resource={{
            id: 0,
            name: systemResource.name,
            public_name: null,
            public_description: systemResource.subtitle ?? null,
            type: "channel_public",
            target_action: "subscribe",
            verification_mode: "no_check",
            is_driver_own: false,
          }}
          status="not_member"
          message={
            systemResourceOpenFailed
              ? {
                  tone: "error",
                  text: "не удалось открыть ссылку. попробуйте ещё раз.",
                }
              : undefined
          }
          onClick={openSystemResource}
          onVerify={openSystemResource}
        />
      </li>,
    );
  }

  return (
    <>
      {customCss ? <style data-tp-flow-design="">{customCss}</style> : null}
      <div
        className="tp-screen tp-showcase-screen"
        data-tp-showcase-root=""
        data-tp-design-contract={SHOWCASE_DESIGN_CONTRACT_VERSION}
        data-all-done={toShowcaseDesignBooleanState(allDone)}
        data-has-final-button={toShowcaseDesignBooleanState(hasFinalButton)}
        data-has-custom-design={toShowcaseDesignBooleanState(
          Boolean(customCss),
        )}
        data-has-error={toShowcaseDesignBooleanState(
          systemResourceOpenFailed ||
            Object.values(resourceActionMessages).some(
              (message) => message.tone === "error",
            ),
        )}
        data-has-visible-resources={toShowcaseDesignBooleanState(
          visibleTaskCount > 0,
        )}
        data-visible-resources={visibleTaskCount}
        data-total-resources={total}
        data-completed-resources={completed}
      >
        <h1
          className="tp-showcase-title"
          data-tp-part={SHOWCASE_DESIGN_PARTS.title}
        >
          <span data-tp-part={SHOWCASE_DESIGN_PARTS.titlePrimary}>
            подпишись на все ресурсы
          </span>
          <span
            className="tp-showcase-title-line"
            data-tp-part={SHOWCASE_DESIGN_PARTS.titleAccent}
          >
            <span
              className="tp-showcase-title-accent-text"
              data-tp-part={SHOWCASE_DESIGN_PARTS.titleAccentText}
            >
              и забирай доступ
            </span>
            <LockIcon
              className="tp-showcase-title-lock"
              size={18}
              designPart={SHOWCASE_DESIGN_PARTS.titleLock}
            />
          </span>
        </h1>

        <ProgressBar
          completed={completed}
          total={total}
          startText={flow.progress_start_text}
          middleText={flow.progress_middle_text}
          finalText={flow.progress_final_text}
        />

        {total === 0 ? (
          <div
            className="tp-card tp-showcase-resource-empty"
            data-tp-part={SHOWCASE_DESIGN_PARTS.resourceEmpty}
            role="status"
          >
            сейчас нет доступных заданий
          </div>
        ) : null}

        <ul
          className="tp-showcase-resource-list"
          data-tp-part={SHOWCASE_DESIGN_PARTS.resourceList}
        >
          {resourceItems}
        </ul>

        {hasFinalButton ? (
          <button
            disabled={!finalReady}
            onClick={() => {
              if (finalReady && returnUrl) openReturnLink(returnUrl);
            }}
            className="tp-showcase-final-button"
            data-tp-part={SHOWCASE_DESIGN_PARTS.finalButton}
            data-ready={toShowcaseDesignBooleanState(finalReady)}
          >
            {!finalReady ? (
              <LockIcon designPart={SHOWCASE_DESIGN_PARTS.finalLock} />
            ) : null}
            <span data-tp-part={SHOWCASE_DESIGN_PARTS.finalText}>
              {flow.final_button_text}
            </span>
          </button>
        ) : null}

        {/* Замок под кнопкой читается как «витрина не прогрузилась»: дублируем
            прогресс текстом, чтобы было видно, сколько заданий осталось. */}
        {hasFinalButton && !finalReady && total > 0 ? (
          <p className="tp-showcase-final-progress" role="status">
            {`выполнено ${completed} из ${total}`}
          </p>
        ) : null}

        {/* Без ссылки возврата кнопки нет вовсе: раньше юзер, выполнивший все
            задания, видел пустую витрину и не понимал, что делать дальше. */}
        {allDone && !hasFinalButton ? (
          <div className="tp-card tp-showcase-final-note" role="status">
            все задания выполнены, вернитесь в бота
          </div>
        ) : null}
      </div>
    </>
  );
}

function systemResourceStorageKey(flowToken: string, revision: string): string {
  return `trafficplace:system-resource:${flowToken}:${revision}`;
}

function readSystemResourceCompleted(
  flowToken: string,
  resource: SystemShowcaseResource | null,
  completedKey: { current: string | null },
): boolean {
  if (!resource) return false;
  const key = systemResourceStorageKey(flowToken, resource.revision);
  if (completedKey.current === key) return true;
  try {
    const completed = window.sessionStorage.getItem(key) === "1";
    if (completed) completedKey.current = key;
    return completed;
  } catch {
    return false;
  }
}

function statusForResource(
  resource: ShowcaseResource,
  local: Record<number, PreCheckStatus>,
  polled: Record<number, PreCheckStatus>,
  explicitVerificationRequired: boolean,
): PreCheckStatus {
  if (local[resource.id] === "system_bot_revoked") {
    return "system_bot_revoked";
  }
  const backendStatus = polled[resource.id];
  if (
    explicitVerificationRequired &&
    (backendStatus === "member" ||
      backendStatus === "not_member" ||
      backendStatus === "system_bot_revoked")
  ) {
    // Explicit flows reconcile against durable DB evidence. MEMBER completes
    // an awaiting-proof checkbox, while NOT_MEMBER is the only durable
    // downgrade allowed to restore an already hidden card.
    return backendStatus;
  }
  if (local[resource.id] === "member") return "member";
  if (
    resource.verification_mode === "no_check" &&
    local[resource.id] === "not_member"
  ) {
    return "not_member";
  }
  if (resource.initial_status === "member") return "member";
  if (backendStatus === "member") return "member";
  if (
    resource.verification_mode === "verified" &&
    backendStatus &&
    backendStatus !== "pending"
  ) {
    return backendStatus;
  }
  const status = local[resource.id] ?? resource.initial_status ?? backendStatus;
  if (
    resource.verification_mode === "no_check" &&
    (!status || status === "pending")
  ) {
    return "not_member";
  }
  return status ?? "pending";
}

type PendingDelivery = {
  token: string | null;
  expiresAt: string | null;
  createdAt: number;
  acknowledgementPending?: boolean;
};

type PendingDeliveries = Record<string, PendingDelivery>;

function pendingDeliveryKey(flowToken: string): string {
  return `trafficplace:no-check-delivery:${flowToken}`;
}

function readPendingDeliveries(flowToken: string): PendingDeliveries {
  try {
    const raw = window.sessionStorage.getItem(pendingDeliveryKey(flowToken));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(parsed).filter(([resourceId, delivery]) => {
        if (
          !/^\d+$/.test(resourceId) ||
          typeof delivery !== "object" ||
          delivery === null
        ) {
          return false;
        }
        const candidate = delivery as Partial<PendingDelivery>;
        const hasToken =
          typeof candidate.token === "string" &&
          candidate.token.length > 0 &&
          candidate.token.length <= 256 &&
          typeof candidate.expiresAt === "string" &&
          Number.isFinite(Date.parse(candidate.expiresAt));
        const awaitingClickResponse =
          candidate.token === null && candidate.expiresAt === null;
        const acknowledgementStateValid =
          candidate.acknowledgementPending === undefined ||
          typeof candidate.acknowledgementPending === "boolean";
        return (
          (hasToken || awaitingClickResponse) &&
          acknowledgementStateValid &&
          typeof candidate.createdAt === "number" &&
          Number.isFinite(candidate.createdAt)
        );
      }),
    ) as PendingDeliveries;
  } catch {
    return {};
  }
}

function writePendingDeliveries(
  flowToken: string,
  deliveries: PendingDeliveries,
): void {
  try {
    const key = pendingDeliveryKey(flowToken);
    if (Object.keys(deliveries).length === 0) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, JSON.stringify(deliveries));
    }
  } catch {
    // Storage can be unavailable in Telegram privacy modes. The in-memory
    // local status still preserves retryability for the current mount.
  }
}

type VerifyResponse = Partial<VerifyOut>;

const VERIFY_REASONS: readonly VerifyReason[] = [
  "verified",
  "definitive_not_member",
  "open_required",
  "source_changed",
  "awaiting_telegram_proof",
  "awaiting_bot_proof",
  "verification_in_progress",
  "provider_unavailable",
  "rate_limited",
];

const VERIFY_NEXT_ACTIONS: readonly VerifyNextAction[] = [
  "none",
  "open_resource",
  "retry_automatically",
  "retry_manually",
];

function parseVerifyResponse(data: unknown): VerifyResponse | undefined {
  if (!data || typeof data !== "object") return undefined;
  const raw = data as Record<string, unknown>;
  const status = isPreCheckStatus(raw.status) ? raw.status : undefined;
  const reason = VERIFY_REASONS.find((value) => value === raw.reason);
  const nextAction = VERIFY_NEXT_ACTIONS.find(
    (value) => value === raw.next_action,
  );
  return {
    verified: typeof raw.verified === "boolean" ? raw.verified : undefined,
    status,
    retryable: typeof raw.retryable === "boolean" ? raw.retryable : undefined,
    retry_after_seconds:
      typeof raw.retry_after_seconds === "number"
        ? raw.retry_after_seconds
        : raw.retry_after_seconds === null
          ? null
          : undefined,
    reason,
    next_action: nextAction,
  };
}

function isPreCheckStatus(value: unknown): value is PreCheckStatus {
  return (
    value === "pending" ||
    value === "member" ||
    value === "not_member" ||
    value === "error" ||
    value === "system_bot_revoked"
  );
}

export function normalizeVerifyResult(data: unknown): {
  status: PreCheckStatus;
  verified: boolean;
} {
  if (!data || typeof data !== "object") {
    return { status: "pending", verified: false };
  }
  const value = parseVerifyResponse(data);
  if (!value) return { status: "pending", verified: false };
  if (value.verified === true || value.status === "member") {
    return { status: "member", verified: true };
  }
  if (value.status === "not_member" && value.retryable !== true) {
    return { status: "not_member", verified: false };
  }
  if (
    value.status === "pending" ||
    value.status === "error" ||
    value.status === "system_bot_revoked"
  ) {
    return { status: value.status, verified: false };
  }
  // Exact predecessor contract: `verified=false` was an explicit upstream
  // non-member result. New retryable responses always carry a tri-state status.
  if (value.verified === false && value.retryable !== true) {
    return { status: "not_member", verified: false };
  }
  return { status: "pending", verified: false };
}

export function verifyRetryMessage(
  data: unknown,
  response: Response | undefined,
): string | null {
  const value = parseVerifyResponse(data);
  if (normalizeVerifyResult(data).status === "not_member") {
    return "Задание пока не выполнено. Выполните его и нажмите галочку ещё раз.";
  }
  const retryAfterSeconds = boundedRetryAfterSeconds(
    value?.retry_after_seconds,
    response,
  );
  switch (value?.reason) {
    case "open_required":
      return "Сначала откройте ресурс и выполните задание, затем нажмите галочку.";
    case "source_changed":
      return "Задание обновилось. Откройте ресурс заново и повторите проверку.";
    case "awaiting_telegram_proof":
      return "Получаем подтверждение от Telegram…";
    case "awaiting_bot_proof":
      return "Ожидаем подтверждение запуска бота…";
    case "verification_in_progress":
      return automaticRetryMessage(
        "Проверяем выполнение задания",
        value.next_action,
        retryAfterSeconds,
      );
    case "provider_unavailable":
      return automaticRetryMessage(
        "Проверка временно недоступна",
        value.next_action,
        retryAfterSeconds,
      );
    case "rate_limited":
      return automaticRetryMessage(
        "Слишком много проверок",
        value.next_action,
        retryAfterSeconds,
      );
  }
  if (value?.next_action === "open_resource") {
    return "Откройте ресурс заново, выполните задание и повторите проверку.";
  }
  if (
    value?.status === "pending" ||
    value?.retryable === true ||
    response?.status === 429
  ) {
    return automaticRetryMessage(
      response?.status === 429
        ? "Слишком много проверок"
        : "Проверка не завершена",
      value?.next_action,
      retryAfterSeconds,
    );
  }
  return null;
}

function automaticRetryMessage(
  prefix: string,
  nextAction: VerifyNextAction | null | undefined,
  retryAfterSeconds: number | null,
): string {
  if (
    nextAction === "retry_automatically" &&
    (retryAfterSeconds === null ||
      retryAfterSeconds <= EXPLICIT_VERIFY_MAX_AUTO_RETRY_SECONDS)
  ) {
    return retryAfterSeconds === null
      ? `${prefix}. Повторим автоматически.`
      : `${prefix}. Повторим автоматически через ${retryAfterSeconds} сек.`;
  }
  return retryAfterSeconds === null
    ? `${prefix}. Повторите через несколько секунд.`
    : `${prefix}. Повторите через ${retryAfterSeconds} сек.`;
}

function explicitVerifyRetryDelayMs(
  data: unknown,
  response: Response | undefined,
): number | null {
  const value = parseVerifyResponse(data);
  if (
    value?.status !== "pending" ||
    value.retryable !== true ||
    (value.next_action !== undefined &&
      value.next_action !== "retry_automatically") ||
    value.reason === "awaiting_telegram_proof" ||
    value.reason === "awaiting_bot_proof"
  ) {
    return null;
  }
  const seconds = boundedRetryAfterSeconds(value.retry_after_seconds, response);
  if (seconds !== null && seconds > EXPLICIT_VERIFY_MAX_AUTO_RETRY_SECONDS) {
    return null;
  }
  return (seconds ?? 1) * 1_000;
}

function awaitsDurableProof(data: unknown): boolean {
  const reason = parseVerifyResponse(data)?.reason;
  return (
    reason === "awaiting_telegram_proof" || reason === "awaiting_bot_proof"
  );
}

function requestsAutomaticFollowup(data: unknown): boolean {
  const value = parseVerifyResponse(data);
  return (
    value?.status === "pending" &&
    (value.next_action === "retry_automatically" || value.retryable === true)
  );
}

function defaultResourceStatusMessage(
  status: PreCheckStatus,
): ResourceActionMessage | undefined {
  if (status === "pending") {
    return {
      epoch: 0,
      tone: "hint",
      text: "Откройте ресурс, выполните задание и затем нажмите галочку.",
    };
  }
  if (status === "error") {
    return {
      epoch: 0,
      tone: "hint",
      text: "Проверка ресурса временно недоступна. Попробуйте ещё раз позже.",
    };
  }
  return undefined;
}

function verificationTemporarilyUnavailableMessage(
  response: Response | undefined,
): string {
  const retryAfterSeconds = boundedRetryAfterSeconds(undefined, response);
  return retryAfterSeconds === null
    ? "Проверка временно недоступна. Повторите через несколько секунд."
    : `Проверка временно недоступна. Повторите через ${retryAfterSeconds} сек.`;
}

function boundedRetryAfterSeconds(
  bodyValue: number | null | undefined,
  response: Response | undefined,
): number | null {
  if (
    typeof bodyValue === "number" &&
    Number.isFinite(bodyValue) &&
    bodyValue > 0
  ) {
    return Math.min(300, Math.max(1, Math.ceil(bodyValue)));
  }
  const header = response?.headers.get("Retry-After")?.trim();
  if (!header) return null;
  if (/^\d+$/.test(header)) {
    return Math.min(300, Math.max(1, Number(header)));
  }
  const retryAt = Date.parse(header);
  if (!Number.isFinite(retryAt)) return null;
  const seconds = Math.ceil((retryAt - Date.now()) / 1000);
  return seconds > 0 ? Math.min(300, seconds) : null;
}

function showcaseBackpressureDelayMs(
  response: Response | undefined,
  failureCount: number,
): number | null {
  if (response?.status !== 429 && response?.status !== 503) return null;
  const exponent = Math.min(Math.max(0, failureCount - 1), 4);
  const exponentialMs = Math.min(
    SHOWCASE_RECONCILE_MAX_BACKOFF_MS,
    SHOWCASE_RECONCILE_MIN_INTERVAL_MS * 2 ** exponent,
  );
  const retryAfterSeconds = boundedRetryAfterSeconds(undefined, response);
  const retryAfterMs = Math.min(
    SHOWCASE_RECONCILE_MAX_BACKOFF_MS,
    (retryAfterSeconds ?? 0) * 1_000,
  );
  return Math.max(exponentialMs, retryAfterMs);
}

function errorMessage(error: unknown, fallback: string): string {
  const detail = errorDetail(error) ?? nestedErrorCode(error);
  if (detail === "period_not_active") {
    return "Ресурс временно недоступен. Попробуйте другой ресурс или вернитесь позже.";
  }
  if (detail === "resource_unavailable") {
    return "Ресурс временно недоступен. Попробуйте другой ресурс или вернитесь позже.";
  }
  if (detail === "resource_inactive") {
    return "Ресурс сейчас недоступен.";
  }
  if (detail === "flow_token_invalid") {
    return "Витрина устарела. Откройте ссылку заново.";
  }
  return fallback;
}

function resourceActionFailureMessage(
  error: unknown,
  response: Response | undefined,
  phase: "click" | "resolve" | "verify",
): string {
  const status = response?.status;
  if (status === 401 || status === 403) {
    return "Сессия устарела. Вернитесь в бота и откройте витрину заново.";
  }
  if (status === 429) {
    const retryAfterSeconds = boundedRetryAfterSeconds(undefined, response);
    return retryAfterSeconds !== null
      ? `Сервис занят. Повторите через ${retryAfterSeconds} сек.`
      : "Сервис занят. Повторите через несколько секунд.";
  }
  if (status !== undefined && status >= 500) {
    return "Сервис временно недоступен. Повторите попытку позже.";
  }

  const detail = errorDetail(error) ?? nestedErrorCode(error);
  if (detail === "redirect_token_gone") {
    return "Ссылка перехода устарела. Нажмите «открыть» ещё раз.";
  }
  if (
    detail === "resource_temporarily_locked" ||
    detail === "temporarily_locked"
  ) {
    return "Ресурс обновляется. Повторите попытку через несколько секунд.";
  }

  const fallback =
    phase === "resolve"
      ? "Не удалось получить ссылку перехода. Повторите попытку."
      : phase === "verify"
        ? "Не удалось проверить ресурс. Повторите попытку."
        : "Не удалось зарегистрировать переход. Повторите попытку.";
  return errorMessage(error, fallback);
}

function nestedErrorCode(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const envelope = (error as Record<string, unknown>).error;
  if (!envelope || typeof envelope !== "object") return null;
  const code = (envelope as Record<string, unknown>).code;
  return typeof code === "string" && code.trim() ? code.trim() : null;
}

function errorDetail(error: unknown): unknown {
  const nested = nestedErrorCode(error);
  if (nested) return nested;
  if (!error || typeof error !== "object") return undefined;
  return "detail" in error ? (error as { detail?: unknown }).detail : undefined;
}

function LockIcon({
  className,
  size = 22,
  designPart,
}: {
  className?: string;
  size?: number;
  designPart: (typeof SHOWCASE_DESIGN_PARTS)["titleLock" | "finalLock"];
}) {
  return (
    <svg
      className={className}
      data-tp-part={designPart}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-6" />
      <path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" />
      <path d="M8 11v-4a4 4 0 1 1 8 0v4" />
    </svg>
  );
}
